import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import ToggleSwitch from '../forms/ToggleSwitch';
import Button from '../ui/Button';
import { getState as getPushState, requestAndRegister, type PushState } from '../../services/webPush';
import './NotificationPreferences.css';

// =============================================================================
// TYPES — mirror of the backend registry, delivered by /api/notifications/catalog
// =============================================================================

export type Channel = 'inApp' | 'email' | 'browser';

interface CatalogType {
  type: string;
  bucket: string;
  audience: 'member' | 'admin';
  channels: Channel[];
  defaults: Record<Channel, boolean>;
  labelKey: string;
  scopeOption: 'eventReminders' | null;
}

interface Catalog {
  channels: Channel[];
  types: CatalogType[];
}

export type ReminderScope = 'off' | 'special' | 'all';

export interface NotificationPrefsValue {
  v: number;
  channels: Partial<Record<Channel, boolean>>;
  types: Record<string, Partial<Record<Channel, boolean>>>;
  eventRemindersScope: ReminderScope;
}

interface Props {
  /** Current value, as returned by the profile endpoint. */
  value: NotificationPrefsValue | undefined;
  /** Legacy global kill switch, honoured until the user saves this matrix. */
  legacySuppressed?: boolean;
  /** Called with the complete next value whenever the user changes something. */
  onChange: (next: NotificationPrefsValue) => void;
}

const CHANNEL_ORDER: Channel[] = ['inApp', 'email', 'browser'];

const emptyValue = (): NotificationPrefsValue => ({
  v: 1,
  channels: {},
  types: {},
  eventRemindersScope: 'all',
});

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * The category x channel notification matrix, shared by the user and admin
 * preferences pages.
 *
 * Rows come from the backend registry rather than a local list, so a new
 * notification type appears here automatically. Rows whose audience is `admin`
 * are hidden from non-admins — a parent has no use for "absence reported".
 */
const NotificationPreferences: React.FC<Props> = ({ value, legacySuppressed, onChange }) => {
  const { t } = useTranslation();
  const { isAdmin, isOwner } = useAuth();
  const canSeeAdminRows = isAdmin || isOwner;

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [pushState, setPushState] = useState<PushState>(() => getPushState());
  const [enablingPush, setEnablingPush] = useState(false);

  const prefs = value ?? emptyValue();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.notifications.catalog();
        if (!cancelled) setCatalog(data as Catalog);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    if (!catalog) return { member: [], admin: [] as CatalogType[] };
    return {
      member: catalog.types.filter(ty => ty.audience === 'member'),
      admin: canSeeAdminRows ? catalog.types.filter(ty => ty.audience === 'admin') : [],
    };
  }, [catalog, canSeeAdminRows]);

  /**
   * Effective state of one cell. Mirrors the backend resolver exactly:
   * master switch AND (per-type override ?? registry default).
   */
  const isOn = useCallback((def: CatalogType, channel: Channel): boolean => {
    if (legacySuppressed) return false;
    if (!def.channels.includes(channel)) return false;
    if (prefs.channels[channel] === false) return false;
    return prefs.types[def.type]?.[channel] ?? def.defaults[channel];
  }, [prefs, legacySuppressed]);

  const setCell = (def: CatalogType, channel: Channel, next: boolean) => {
    const types = { ...prefs.types, [def.type]: { ...prefs.types[def.type], [channel]: next } };
    // Turning a cell on while its column master is off would be a no-op the
    // user cannot see, so lift the master too.
    const channels = next && prefs.channels[channel] === false
      ? { ...prefs.channels, [channel]: true }
      : prefs.channels;
    onChange({ ...prefs, v: 1, types, channels });
  };

  /** Column header switch: set every visible row in this column at once. */
  const setColumn = (channel: Channel, next: boolean) => {
    const visible = [...rows.member, ...rows.admin];
    const types = { ...prefs.types };
    for (const def of visible) {
      if (!def.channels.includes(channel)) continue;
      types[def.type] = { ...types[def.type], [channel]: next };
    }
    onChange({
      ...prefs,
      v: 1,
      types,
      channels: { ...prefs.channels, [channel]: next ? true : prefs.channels[channel] ?? true },
    });
  };

  const columnAllOn = (channel: Channel): boolean => {
    const visible = [...rows.member, ...rows.admin].filter(d => d.channels.includes(channel));
    return visible.length > 0 && visible.every(d => isOn(d, channel));
  };

  const [pushError, setPushError] = useState<string | null>(null);

  const handleEnablePush = async () => {
    setEnablingPush(true);
    setPushError(null);
    try {
      const result = await requestAndRegister();
      setPushState(result.state);
      // Permission granted but no token stored means nothing can be delivered.
      // Saying nothing would leave the column looking enabled while it is not.
      if (result.state === 'granted' && !result.registered) {
        setPushError(t('notificationPrefs.browserFailed'));
      }
    } finally {
      setEnablingPush(false);
    }
  };

  const browserUsable = pushState === 'granted';

  const browserNotice = (): string | null => {
    switch (pushState) {
      case 'unsupported': return t('notificationPrefs.browserUnsupported');
      case 'not-configured': return t('notificationPrefs.browserNotConfigured');
      case 'denied': return t('notificationPrefs.browserBlocked');
      case 'granted': return t('notificationPrefs.browserEnabled');
      default: return t('notificationPrefs.browserPrompt');
    }
  };

  if (loadError) {
    return (
      <div className="notif-prefs">
        <p className="notif-prefs__error">{t('notificationPrefs.saveError')}</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="notif-prefs">
        <p className="notif-prefs__muted">{t('common.loading', 'Loading…')}</p>
      </div>
    );
  }

  const renderRow = (def: CatalogType) => (
    <div
      className="notif-prefs__row"
      key={def.type}
      data-testid="notif-row"
      data-type={def.type}
      data-audience={def.audience}
    >
      <div className="notif-prefs__rowLabel">
        <span className="notif-prefs__rowTitle">
          {t(`notificationTypes.${def.labelKey}.label`)}
        </span>
        <span className="notif-prefs__rowDesc">
          {t(`notificationTypes.${def.labelKey}.description`)}
        </span>

        {def.scopeOption === 'eventReminders' && (
          <label className="notif-prefs__scope">
            <span className="notif-prefs__scopeLabel">
              {t('notificationPrefs.reminderScope')}
            </span>
            <select
              className="notif-prefs__select"
              value={prefs.eventRemindersScope}
              onChange={(e) =>
                onChange({ ...prefs, v: 1, eventRemindersScope: e.target.value as ReminderScope })
              }
            >
              <option value="all">{t('preferences.eventRemindersAll')}</option>
              <option value="special">{t('preferences.eventRemindersSpecial')}</option>
              <option value="off">{t('preferences.eventRemindersOff')}</option>
            </select>
          </label>
        )}
      </div>

      {CHANNEL_ORDER.map((channel) => {
        const supported = def.channels.includes(channel);
        const disabled = !supported || (channel === 'browser' && !browserUsable);
        return (
          <div
            className="notif-prefs__cell"
            key={channel}
            data-testid="notif-cell"
            data-channel={channel}
            data-state={supported ? (isOn(def, channel) ? 'on' : 'off') : 'unsupported'}
            data-disabled={disabled ? 'true' : 'false'}
            data-label={t(`notificationPrefs.column${channel === 'inApp' ? 'InApp' : channel === 'email' ? 'Email' : 'Browser'}`)}
          >
            {supported ? (
              <ToggleSwitch
                checked={isOn(def, channel)}
                disabled={disabled}
                onChange={(next) => setCell(def, channel, next)}
              />
            ) : (
              <span className="notif-prefs__na" aria-hidden="true">—</span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="notif-prefs">
      {/* Browser channel status + the only place permission is ever requested */}
      <div className="notif-prefs__browserBar" data-testid="notif-browser-bar" data-push-state={pushState}>
        <span className={pushError ? 'notif-prefs__error' : 'notif-prefs__muted'}>
          {pushError ?? browserNotice()}
        </span>
        {pushState === 'default' && (
          <Button
            variant="secondary"
            onClick={handleEnablePush}
            disabled={enablingPush}
          >
            {enablingPush
              ? t('notificationPrefs.browserEnabling')
              : t('notificationPrefs.browserEnable')}
          </Button>
        )}
      </div>

      <div className="notif-prefs__table" data-testid="notif-table" role="group" aria-label={t('notificationPrefs.title')}>
        <div className="notif-prefs__head">
          <div className="notif-prefs__rowLabel" />
          {CHANNEL_ORDER.map((channel) => {
            const key = channel === 'inApp' ? 'InApp' : channel === 'email' ? 'Email' : 'Browser';
            const allOn = columnAllOn(channel);
            return (
              <div className="notif-prefs__cell notif-prefs__cell--head" key={channel}>
                <span className="notif-prefs__colTitle">
                  {t(`notificationPrefs.column${key}`)}
                </span>
                <span className="notif-prefs__colHint">
                  {t(`notificationPrefs.column${key}Hint`)}
                </span>
                <button
                  type="button"
                  className="notif-prefs__colToggle"
                  data-testid="notif-col-toggle"
                  data-channel={channel}
                  onClick={() => setColumn(channel, !allOn)}
                  disabled={channel === 'browser' && !browserUsable}
                >
                  {allOn ? t('notificationPrefs.allOff') : t('notificationPrefs.allOn')}
                </button>
              </div>
            );
          })}
        </div>

        <h4 className="notif-prefs__section">{t('notificationPrefs.memberSection')}</h4>
        {rows.member.map(renderRow)}

        {rows.admin.length > 0 && (
          <>
            <h4 className="notif-prefs__section">{t('notificationPrefs.adminSection')}</h4>
            {rows.admin.map(renderRow)}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationPreferences;
